import {
  Button,
  Divider,
  Empty,
  Modal,
  PrioSpinner,
  Segmented,
  useNotification,
  useTheme,
} from '@prio365/prio365-react-library';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getProject,
  getUserMeContactId,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import Flex from '../../../../components/Flex';
import { Project } from '../../../../models/Project';
import { PrioShareDto } from '../../../../models/PrioShare';
import { ContactId, ProjectId } from '../../../../models/Types';
import { makePrioStyles } from '../../../../theme/utils';
import * as ConfigValues from '../../../../util/configValues';
import { useDeactivatePrioShare } from './hooks/useDeactivatePrioShare';
import { useDeletePrioShareDraft } from './hooks/useDeletePrioShareDraft';
import PrioShareItemCard from './PrioShareItemCard';
import usePrioShareData from './hooks/usePrioShareData';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  itemContainer: {
    overflowY: 'auto',
    maxHeight: '100vh',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.small,
    width: '100%',
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
  },
  prioIcon: {
    width: 16,
    height: 16,
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.muted,
    paddingBottom: theme.spacing.small,
    borderBottom: `1px solid ${theme.colors.application.border}`,
  },
}));

type ActiveTab = 'active' | 'archived' | 'drafts';

interface PrioShareSectionProps {
  projectId: ProjectId;
  projectExtensionId: string;
  onView: (item: PrioShareDto) => void;
  onEditDraft: (item: PrioShareDto) => void;
}

const PrioShareSection: React.FC<PrioShareSectionProps> = ({
  projectId,
  projectExtensionId,
  onView,
  onEditDraft,
}) => {
  //#region ------------------------------ Defaults
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const [notification, notificationContextHolder] = useNotification();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [activeTab, setActiveTab] = useState<ActiveTab>('active');
  const [ownerFilter, setOwnerFilter] = useState<'all' | 'mine'>('all');
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    type: 'deactivate' | 'deleteDraft';
    item: PrioShareDto | null;
  }>({ visible: false, type: 'deactivate', item: null });

  const { prioShares, isLoading, isError } =
    usePrioShareData(projectExtensionId);
  const { mutate: deactivateShare } = useDeactivatePrioShare();
  const { mutate: deleteDraft } = useDeletePrioShareDraft();

  const meContactId = useSelector<RootReducerState, ContactId>((state) =>
    getUserMeContactId(state)
  );

  const project = useSelector<RootReducerState, Project>((state) =>
    getProject(state, projectId)
  );
  const projectName = project
    ? [project.number, project.shortName].filter(Boolean).join(' ')
    : undefined;

  const drafts = useMemo(
    () => prioShares.filter((s) => s.state === 'draft'),
    [prioShares]
  );

  const effectiveTab =
    activeTab === 'drafts' && drafts.length === 0 ? 'active' : activeTab;

  const filteredShares = useMemo(
    () =>
      (effectiveTab === 'active'
        ? prioShares.filter((s) => s.state === 'active')
        : effectiveTab === 'archived'
          ? prioShares.filter(
              (s) => s.state === 'expired' || s.state === 'inactive'
            )
          : drafts
      ).filter(
        (s) =>
          effectiveTab === 'drafts' ||
          ownerFilter === 'all' ||
          s.createdBy === meContactId
      ),
    [prioShares, effectiveTab, drafts, ownerFilter, meContactId]
  );
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleView = useCallback(
    (item: PrioShareDto) => {
      onView(item);
    },
    [onView]
  );

  const handleCopyLink = useCallback(
    (prioShareId: string) => {
      const isLocal =
        ConfigValues.REACT_APP_API_REDIRECT_URL.includes('prio.local');
      const baseUrl = isLocal
        ? 'https://next.prio365.fc-digitalisierung.de'
        : `https://next.${ConfigValues.REACT_APP_API_REDIRECT_URL.replace(/^https?:\/\//, '').replace(/\/$/, '')}`;
      const link = `${baseUrl}/share/prioShare/${prioShareId}`;

      navigator.clipboard
        .writeText(link)
        .then(() => {
          notification.success({
            title: t('common:widgetArea.prioShareWidget.linkCopied'),
          });
        })
        .catch(() => {
          notification.error({
            title: t('common:widgetArea.prioShareWidget.linkCopyError'),
          });
        });
    },
    [notification, t]
  );

  const handleDeactivate = useCallback((item: PrioShareDto) => {
    setConfirmModal({ visible: true, type: 'deactivate', item });
  }, []);

  const handleDeleteDraft = useCallback((item: PrioShareDto) => {
    setConfirmModal({ visible: true, type: 'deleteDraft', item });
  }, []);

  const handleConfirmModalOk = useCallback(() => {
    const { type, item } = confirmModal;
    if (!item) return;
    setConfirmModal((prev) => ({ ...prev, visible: false }));

    if (type === 'deactivate') {
      deactivateShare(
        { prioShareId: item.prioShareId, projectExtensionId },
        {
          onSuccess: () => {
            notification.success({
              title: t('common:widgetArea.prioShareWidget.deactivateSuccess'),
            });
          },
          onError: () => {
            notification.error({
              title: t('common:widgetArea.prioShareWidget.deactivateError'),
            });
          },
        }
      );
    } else {
      deleteDraft(
        { prioShareId: item.prioShareId, projectExtensionId },
        {
          onSuccess: () => {
            notification.success({
              title: t('common:widgetArea.prioShareWidget.deleteDraftSuccess'),
            });
          },
          onError: () => {
            notification.error({
              title: t('common:widgetArea.prioShareWidget.deleteDraftError'),
            });
          },
        }
      );
    }
  }, [
    confirmModal,
    deactivateShare,
    deleteDraft,
    notification,
    projectExtensionId,
    t,
  ]);

  const handleConfirmModalClose = useCallback(() => {
    setConfirmModal((prev) => ({ ...prev, visible: false }));
  }, []);
  //#endregion

  return (
    <>
      {notificationContextHolder}
      <Segmented
        key={drafts.length > 0 ? 'with-drafts' : 'without-drafts'}
        value={effectiveTab}
        fillContainer={true}
        onChange={(val) => setActiveTab(val as ActiveTab)}
        options={[
          {
            label: t('common:widgetArea.prioShareWidget.tabActive'),
            value: 'active',
          },
          {
            label: t('common:widgetArea.prioShareWidget.tabArchived'),
            value: 'archived',
          },
          ...(drafts.length > 0
            ? [
                {
                  label: t('common:widgetArea.prioShareWidget.tabDrafts'),
                  value: 'drafts' as ActiveTab,
                },
              ]
            : []),
        ]}
      />
      {effectiveTab !== 'drafts' && (
        <div
          className={classes.filterRow}
          onClick={() =>
            setOwnerFilter((prev) => (prev === 'all' ? 'mine' : 'all'))
          }
        >
          <span>
            {ownerFilter === 'all'
              ? t('common:widgetArea.prioShareWidget.filterAll')
              : t('common:widgetArea.prioShareWidget.filterMine')}
          </span>
          <Button iconProp={['fal', 'bars-filter']} type="link" size="small" />
        </div>
      )}
      <Flex.Column className={classes.itemContainer}>
        {isLoading ? (
          <Flex.Row justifyContent="center">
            <PrioSpinner size="large" />
          </Flex.Row>
        ) : isError ? (
          <div style={{ paddingTop: 24, paddingBottom: 24 }}>
            <Empty
              customMessage={t('common:widgetArea.prioShareWidget.loadError')}
            />
          </div>
        ) : filteredShares.length === 0 ? (
          <div style={{ paddingTop: 24, paddingBottom: 24 }}>
            <Empty
              customMessage={
                effectiveTab === 'drafts'
                  ? t('common:widgetArea.prioShareWidget.emptyDrafts')
                  : effectiveTab === 'archived'
                    ? t('common:widgetArea.prioShareWidget.emptyArchived')
                    : t('common:widgetArea.prioShareWidget.emptyActive')
              }
            />
          </div>
        ) : (
          filteredShares.map((item, index) => (
            <React.Fragment key={item.prioShareId}>
              <PrioShareItemCard
                item={item}
                projectName={projectName}
                onView={handleView}
                onCopyLink={handleCopyLink}
                onDeactivate={handleDeactivate}
                onEdit={onEditDraft}
                onDelete={handleDeleteDraft}
              />
              {index < filteredShares.length - 1 && (
                <Divider style={{ marginBlock: theme.spacing.small }} />
              )}
            </React.Fragment>
          ))
        )}
      </Flex.Column>

      {/* Confirm Modal for deactivate / delete draft */}
      <Modal
        title={
          confirmModal.type === 'deactivate'
            ? t('common:widgetArea.prioShareWidget.deactivateConfirm')
            : t('common:widgetArea.prioShareWidget.deleteDraftConfirm')
        }
        visible={confirmModal.visible}
        onOk={handleConfirmModalOk}
        onClose={handleConfirmModalClose}
        okText={
          confirmModal.type === 'deactivate'
            ? t('common:widgetArea.prioShareWidget.deactivateConfirmOk')
            : t('common:widgetArea.prioShareWidget.deleteDraftConfirmOk')
        }
        cancelText={t('common:cancel')}
        okButtonProps={{ type: 'danger' }}
      >
        {confirmModal.type === 'deactivate'
          ? t('common:widgetArea.prioShareWidget.deactivateConfirmContent')
          : t('common:widgetArea.prioShareWidget.deleteDraftConfirmContent')}
      </Modal>
    </>
  );
};

export default PrioShareSection;
